// Gradient sky dome with a blocky square sun, twinkling stars and drifting voxel clouds.
import * as THREE from 'three';
import { hash2 } from './util.js';

const skyVert = /* glsl */ `
varying vec3 vDir;
void main() {
  vDir = position;
  vec4 p = projectionMatrix * modelViewMatrix * vec4(position, 1.0);
  gl_Position = p.xyww;
}`;

const skyFrag = /* glsl */ `
uniform vec3 topColor;
uniform vec3 horizonColor;
uniform vec3 bottomColor;
uniform vec3 sunColor;
uniform vec3 sunDir;
uniform float sunSize;
uniform float stars;
uniform float time;
uniform vec3 nebula;
varying vec3 vDir;
float hash(vec3 p) { p = fract(p * 0.3183099 + 0.1); p *= 17.0; return fract(p.x * p.y * p.z * (p.x + p.y + p.z)); }
void main() {
  vec3 d = normalize(vDir);
  float h = d.y;
  vec3 col = h > 0.0 ? mix(horizonColor, topColor, pow(clamp(h, 0.0, 1.0), 0.55)) : mix(horizonColor, bottomColor, pow(clamp(-h, 0.0, 1.0), 0.45));
  // square sun
  vec3 sd = normalize(sunDir);
  vec3 r = normalize(cross(sd, vec3(0.0, 1.0, 0.0001)));
  vec3 u = cross(r, sd);
  float facing = dot(d, sd);
  if (facing > 0.0) {
    vec2 q = vec2(dot(d, r), dot(d, u)) / facing;
    float m = max(abs(q.x), abs(q.y));
    float disc = 1.0 - smoothstep(sunSize, sunSize + 0.004, m);
    col = mix(col, sunColor * 1.8, disc);
    col += sunColor * 0.32 * pow(max(facing, 0.0), 14.0);
  }
  if (stars > 0.0) {
    vec3 p = floor(d * 260.0);
    float s = hash(p);
    float tw = 0.6 + 0.4 * sin(time * 2.5 + s * 80.0);
    col += vec3(step(0.9975, s) * stars * tw) * smoothstep(-0.1, 0.25, h);
    // soft nebula bands
    float n = sin(d.x * 3.0 + d.z * 2.0 + 1.3) * sin(d.y * 4.0 - d.x * 2.0) * 0.5 + 0.5;
    col += nebula * pow(n, 3.0) * stars * 0.6;
  }
  gl_FragColor = vec4(col, 1.0);
}`;

export class Sky {
  constructor(scene) {
    this.scene = scene;
    this.uniforms = {
      topColor: { value: new THREE.Color('#2f7fd6') },
      horizonColor: { value: new THREE.Color('#b8e0ff') },
      bottomColor: { value: new THREE.Color('#d8eeff') },
      sunColor: { value: new THREE.Color('#fff4d0') },
      sunDir: { value: new THREE.Vector3(0.4, 0.55, 0.35) },
      sunSize: { value: 0.06 },
      stars: { value: 0 },
      time: { value: 0 },
      nebula: { value: new THREE.Color('#000000') },
    };
    const mat = new THREE.ShaderMaterial({
      vertexShader: skyVert,
      fragmentShader: skyFrag,
      uniforms: this.uniforms,
      side: THREE.BackSide,
      depthWrite: false,
      depthTest: true,
      fog: false,
    });
    this.dome = new THREE.Mesh(new THREE.SphereGeometry(900, 32, 16), mat);
    this.dome.frustumCulled = false;
    this.dome.renderOrder = -10;
    scene.add(this.dome);
    this.cloudGroups = [];
    this.drift = 0;
    // A few distant birds add life to every outdoor world using a single draw call.
    this.birdPositions = new Float32Array(7 * 4 * 3);
    const birdsGeo = new THREE.BufferGeometry(); birdsGeo.setAttribute('position',new THREE.BufferAttribute(this.birdPositions,3));
    this.birds = new THREE.LineSegments(birdsGeo,new THREE.LineBasicMaterial({color:'#40586b',transparent:true,opacity:.5,fog:true}));
    this.birds.frustumCulled=false; scene.add(this.birds);
  }

  setTheme(sky) {
    const u = this.uniforms;
    u.topColor.value.set(sky.top);
    u.horizonColor.value.set(sky.horizon);
    u.bottomColor.value.set(sky.bottom ?? sky.horizon);
    u.sunColor.value.set(sky.sun ?? '#fff4d0');
    u.sunSize.value = sky.sunSize ?? 0.06;
    u.stars.value = sky.stars ?? 0;
    u.nebula.value.set(sky.nebula ?? '#000000');
    if (sky.sunDir) u.sunDir.value.set(...sky.sunDir).normalize();
    this.buildClouds(sky.clouds ?? [], sky.cloudColor ?? '#ffffff', sky.cloudOpacity ?? 0.88);
  }

  // Soft clouds: a scrolling "sea of clouds" far below the islands + fluffy puffs up high.
  // (No blocky clouds — nothing ever sits near the course or the camera.)
  buildClouds(layers, color, opacity) {
    for (const g of this.cloudGroups) {
      this.scene.remove(g);
      g.traverse((o) => {
        if (o.geometry && !o.geometry.userData.shared) o.geometry.dispose();
        if (o.material && o.material !== this.seaMat && o.material !== this.puffMat) o.material.dispose();
      });
    }
    this.cloudGroups = [];
    if (!layers.length) return;
    if (!this.seaTex) {
      this.seaTex = cloudSeaTexture();
      this.puffTex = cloudPuffTexture();
    }
    const col = new THREE.Color(color);
    // sea of clouds below
    const below = layers.filter((l) => l.y < 40);
    for (const L of below) {
      const tex = this.seaTex.clone();
      tex.needsUpdate = true;
      tex.wrapS = tex.wrapT = THREE.RepeatWrapping;
      tex.repeat.set(10, 10);
      const mat = new THREE.MeshBasicMaterial({ map: tex, color: col, transparent: true, opacity: Math.min(1, opacity + 0.05), depthWrite: false, fog: true });
      const sea = new THREE.Mesh(new THREE.PlaneGeometry(2400, 2400), mat);
      sea.rotation.x = -Math.PI / 2;
      sea.position.y = L.y;
      sea.renderOrder = -2;
      const g = new THREE.Group();
      g.add(sea);
      g.userData = { sea: tex, speed: (L.speed ?? 1) * 0.0018 };
      this.scene.add(g);
      this.cloudGroups.push(g);
      // a second, offset layer for depth
      const tex2 = tex.clone();
      tex2.needsUpdate = true;
      tex2.repeat.set(7, 7);
      tex2.offset.set(0.37, 0.61);
      const sea2 = new THREE.Mesh(sea.geometry, new THREE.MeshBasicMaterial({ map: tex2, color: col.clone().multiplyScalar(0.92), transparent: true, opacity: opacity * 0.8, depthWrite: false, fog: true }));
      sea2.rotation.x = -Math.PI / 2;
      sea2.position.y = L.y - 6;
      sea2.renderOrder = -3;
      g.add(sea2);
      g.userData.sea2 = tex2;
    }
    // fluffy puffs up high
    const above = layers.filter((l) => l.y >= 40);
    for (const L of above) {
      const g = new THREE.Group();
      const n = Math.round(10 + L.coverage * 30);
      let seed = L.seed * 9301 + 49297;
      const rnd = () => ((seed = (seed * 9301 + 49297) % 233280) / 233280);
      for (let i = 0; i < n; i++) {
        const mat = new THREE.SpriteMaterial({ map: this.puffTex, color: col, transparent: true, opacity: opacity * (0.75 + rnd() * 0.25), depthWrite: false, fog: true });
        const sp = new THREE.Sprite(mat);
        const w = 50 + rnd() * 70;
        sp.scale.set(w, w * 0.42, 1);
        sp.position.set((rnd() - 0.5) * 700, L.y + rnd() * 30, -250 + rnd() * 800);
        g.add(sp);
      }
      g.userData = { puffs: true, speed: (L.speed ?? 1) * 1.2 };
      this.scene.add(g);
      this.cloudGroups.push(g);
    }
  }

  update(dt, camera) {
    this.uniforms.time.value += dt;
    this.dome.position.copy(camera.position);
    this.drift += dt;
    this.birds.visible=this.uniforms.stars.value===0 && this.uniforms.topColor.value.b>.2;
    for(let i=0;i<7;i++) {
      const a=this.drift*.035+i*.28,x=camera.position.x+Math.sin(a)*55,z=camera.position.z+Math.cos(a)*55,y=camera.position.y+20+i*.45,flap=Math.sin(this.drift*3.2+i)*.5;
      this.birdPositions.set([x-.8,y+flap,z,x,y,z+.25,x,y,z+.25,x+.8,y+flap,z],i*12);
    }
    this.birds.geometry.attributes.position.needsUpdate=true;
    for (const g of this.cloudGroups) {
      const u = g.userData;
      if (u.sea) {
        u.sea.offset.x = (this.drift * u.speed) % 1;
        g.position.x = camera.position.x;
        g.position.z = camera.position.z;
        // keep the texture fixed in the world while the plane follows the camera
        const k = 10 / 2400, k2 = 7 / 2400;
        u.sea.offset.y = (-camera.position.z * k) % 1;
        u.sea.offset.x = (u.sea.offset.x + camera.position.x * k) % 1;
        if (u.sea2) {
          u.sea2.offset.x = (0.37 + this.drift * u.speed * 0.7 + camera.position.x * k2) % 1;
          u.sea2.offset.y = (0.61 - camera.position.z * k2) % 1;
        }
      } else if (u.puffs) {
        for (const sp of g.children) {
          sp.position.x += u.speed * dt;
          if (sp.position.x > camera.position.x + 350) sp.position.x -= 700;
        }
      }
    }
  }

  dispose() {
    for (const g of this.cloudGroups) {
      this.scene.remove(g);
      g.traverse((o) => o.geometry && o.geometry.dispose());
    }
    this.cloudGroups = [];
    this.scene.remove(this.birds);this.birds.geometry.dispose();this.birds.material.dispose();
  }
}

// ---- soft cloud textures (canvas) ----
function cloudSeaTexture() {
  const S = 256;
  const c = document.createElement('canvas');
  c.width = c.height = S;
  const ctx = c.getContext('2d');
  const img = ctx.createImageData(S, S);
  // tileable value noise (fbm)
  const g = 16;
  const rnd = [];
  for (let i = 0; i < g * g; i++) rnd.push(hash2(i % g, Math.floor(i / g), 7));
  const at = (x, y, gs) => {
    const k = g / gs;
    const xi = Math.floor(x), yi = Math.floor(y), xf = x - xi, yf = y - yi;
    const w = (a) => ((a % gs) + gs) % gs;
    const v = (a, b) => rnd[(w(b) * k) * g + w(a) * k];
    const u = xf * xf * (3 - 2 * xf), t = yf * yf * (3 - 2 * yf);
    return (v(xi, yi) * (1 - u) + v(xi + 1, yi) * u) * (1 - t) + (v(xi, yi + 1) * (1 - u) + v(xi + 1, yi + 1) * u) * t;
  };
  for (let y = 0; y < S; y++)
    for (let x = 0; x < S; x++) {
      let n = 0, amp = 0.55, tot = 0;
      for (const gs of [4, 8, 16]) {
        n += at((x / S) * gs, (y / S) * gs, gs) * amp;
        tot += amp;
        amp *= 0.5;
      }
      n /= tot;
      const a = Math.max(0, Math.min(1, (n - 0.36) * 2.6));
      const i = (y * S + x) * 4;
      const shade = 225 + n * 30;
      img.data[i] = shade;
      img.data[i + 1] = shade;
      img.data[i + 2] = Math.min(255, shade + 8);
      img.data[i + 3] = Math.round(a * 255);
    }
  ctx.putImageData(img, 0, 0);
  const t = new THREE.CanvasTexture(c);
  t.colorSpace = THREE.SRGBColorSpace;
  t.wrapS = t.wrapT = THREE.RepeatWrapping;
  return t;
}

function cloudPuffTexture() {
  const W = 256, H = 128;
  const c = document.createElement('canvas');
  c.width = W;
  c.height = H;
  const ctx = c.getContext('2d');
  const blobs = [[0.5, 0.55, 0.32], [0.32, 0.62, 0.22], [0.68, 0.62, 0.24], [0.42, 0.42, 0.22], [0.6, 0.4, 0.2], [0.2, 0.7, 0.14], [0.8, 0.7, 0.15]];
  for (const [bx, by, br] of blobs) {
    const gr = ctx.createRadialGradient(bx * W, by * H, 0, bx * W, by * H, br * W);
    gr.addColorStop(0, 'rgba(255,255,255,0.95)');
    gr.addColorStop(0.55, 'rgba(250,252,255,0.75)');
    gr.addColorStop(1, 'rgba(240,245,255,0)');
    ctx.fillStyle = gr;
    ctx.fillRect(0, 0, W, H);
  }
  // soft grey underside
  const sh = ctx.createLinearGradient(0, H * 0.45, 0, H);
  sh.addColorStop(0, 'rgba(0,0,0,0)');
  sh.addColorStop(1, 'rgba(120,140,170,0.25)');
  ctx.globalCompositeOperation = 'source-atop';
  ctx.fillStyle = sh;
  ctx.fillRect(0, 0, W, H);
  const t = new THREE.CanvasTexture(c);
  t.colorSpace = THREE.SRGBColorSpace;
  return t;
}
